/**
 * Shared helpers for interpreting raw coverage status strings from EV / Postgres
 * into normalized codes used by the coverage UI.
 *
 * Two distinct columns are handled here and must not be conflated:
 * - `coverage.coverage_status` → active/inactive plan status
 *   ({@link determineStatusCodeForCoverage}).
 * - `coverage.status` → the EV flow status enum ({@link mapCoverageStatus}).
 */
import { CoveragePlanDisplayStatus } from "../../../enums/coverageInformationEnums";
import { CoverageStatus } from "../../../enums/coverageStatus";

/**
 * Normalized plan-activity status derived from coverage data, as opposed to the
 * EV flow status ({@link CoverageStatus}).
 */
export type PlanActivityStatus = "active" | "inactive" | "unknown";

/**
 * Determines whether a coverage entry is active, inactive, or unknown
 * based on its raw `coverage_status` string from the database.
 *
 * @param status - Raw coverage status string from the database
 * @returns The normalized plan-activity status
 */
export const determineStatusCodeForCoverage = (
  status?: string,
): PlanActivityStatus => {
  if (status && typeof status === "string") {
    if (status === "") return "inactive";
    if (status === "-") return "unknown";
    if (status.toLowerCase().includes("inactive")) return "inactive";
    if (status.toLowerCase().includes("active")) return "active";
    return "unknown";
  }
  return "unknown";
};

/**
 * Derives the active/inactive/unknown plan status from the display enum, so
 * consumers can work from the {@link CoveragePlanDisplayStatus} that is already
 * carried on the coverage view model instead of re-reading the raw column.
 *
 * @param planStatus - Plan display status from the coverage view model
 * @returns The normalized plan-activity status
 */
export const getPlanActivityStatus = (
  planStatus: CoveragePlanDisplayStatus | undefined,
): PlanActivityStatus => {
  if (planStatus === CoveragePlanDisplayStatus.ACTIVE) return "active";
  if (planStatus === CoveragePlanDisplayStatus.INACTIVE) return "inactive";
  return "unknown";
};

/**
 * Maps a raw `coverage.status` string from the database to the CoverageStatus
 * enum. This is the EV flow status (in progress, approved, error, ...), which is
 * distinct from the active/inactive {@link determineStatusCodeForCoverage}.
 *
 * @param status - Raw status string (e.g. "in_progress", "error")
 * @returns The corresponding CoverageStatus enum value
 */
export const mapCoverageStatus = (status: string): CoverageStatus => {
  const enumValue = Object.values(CoverageStatus).find(
    (value) => value.toLowerCase() === status.toLowerCase(),
  );
  if (enumValue) {
    return enumValue;
  }
  // The case-insensitive match above already resolves every value whose
  // spelling matches an enum member (pending, approved, rejected, error,
  // completed, inactive, ...). Only these underscored raw values differ from
  // their enum members ("In Progress" / "EV Type Not Supported") and fall
  // through, so they need explicit handling.
  if (status === "in_progress") return CoverageStatus.IN_PROGRESS;
  if (status === "ev_type_not_supported")
    return CoverageStatus.EV_TYPE_NOT_SUPPORTED;
  if (status.toLowerCase().includes("inactive")) return CoverageStatus.INACTIVE;
  if (status.toLowerCase().includes("error")) return CoverageStatus.ERROR;
  return CoverageStatus.UNKNOWN;
};
